import React, { useState, useEffect } from 'react';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { 
    faBell, faSearch, faFilter, faExclamationTriangle, 
    faInfoCircle, faCheckCircle, faClock, faEye
} from '@fortawesome/free-solid-svg-icons';
import DashboardHeader from '../../layouts/dashboard_header';
import SideMenu from '../../layouts/dashboard_menu';
import DashboardFooter from '../../layouts/dashboard_footer';
import localForage from 'localforage';

interface Notice {
    id: string;
    title: string;
    content: string;
    type: 'info' | 'warning' | 'success' | 'error';
    priority: 'low' | 'medium' | 'high';
    createdAt: string;
    expiresAt?: string;
    isRead: boolean;
    category: 'system' | 'maintenance' | 'update' | 'announcement';
}

const Notices: React.FC = () => {
    const [notices, setNotices] = useState<Notice[]>([]);
    const [filteredNotices, setFilteredNotices] = useState<Notice[]>([]);
    const [searchQuery, setSearchQuery] = useState<string>('');
    const [typeFilter, setTypeFilter] = useState<string>('all');
    const [categoryFilter, setCategoryFilter] = useState<string>('all');
    const [clinicName, setClinicName] = useState<string>('Facility');

    useEffect(() => {
        document.title = "System Notices - Uzima Healthcare";
        loadClinicData();
        loadNotices();
    }, []);

    useEffect(() => {
        filterNotices();
    }, [notices, searchQuery, typeFilter, categoryFilter]);

    const loadClinicData = async () => {
        try {
            const storedClinicName = await localForage.getItem('clinic_name');
            if (storedClinicName) {
                setClinicName(storedClinicName as string);
            }
        } catch (error) {
            console.error('Error loading clinic data:', error);
        }
    };

    const loadNotices = () => {
        // Mock notices data - in real app, fetch from API
        const mockNotices: Notice[] = [
            {
                id: 'NOTICE-001',
                title: 'Scheduled System Maintenance',
                content: 'We will be performing scheduled maintenance on our servers on September 10th, 2025 from 2:00 AM to 4:00 AM EST. During this time, some features may be temporarily unavailable.',
                type: 'warning',
                priority: 'high',
                createdAt: '2025-09-05T10:00:00Z',
                expiresAt: '2025-09-10T06:00:00Z',
                isRead: false,
                category: 'maintenance'
            },
            {
                id: 'NOTICE-002',
                title: 'New Telehealth Features Available',
                content: 'We have released new telehealth features including session recording, breakout rooms, and enhanced participant management. Check out the updated documentation for more details.',
                type: 'success',
                priority: 'medium',
                createdAt: '2025-09-04T14:30:00Z',
                isRead: true,
                category: 'update'
            },
            {
                id: 'NOTICE-003',
                title: 'Billing System Update',
                content: 'Our billing system has been updated with improved invoice generation and payment processing. All existing billing data has been migrated successfully.',
                type: 'info',
                priority: 'medium',
                createdAt: '2025-09-03T09:15:00Z',
                isRead: true,
                category: 'system'
            },
            {
                id: 'NOTICE-004',
                title: 'Security Enhancement',
                content: 'We have implemented additional security measures including two-factor authentication for admin accounts. Please ensure your account is properly secured.',
                type: 'info',
                priority: 'high',
                createdAt: '2025-09-02T16:45:00Z',
                isRead: false,
                category: 'announcement'
            },
            {
                id: 'NOTICE-005',
                title: 'API Rate Limiting Update',
                content: 'API rate limits have been updated to improve system performance. Please review the updated API documentation for new limits and best practices.',
                type: 'warning',
                priority: 'medium',
                createdAt: '2025-09-01T11:20:00Z',
                isRead: true,
                category: 'system'
            }
        ];
        setNotices(mockNotices);
    };

    const filterNotices = () => {
        let filtered = notices;

        // Filter by search query
        if (searchQuery) {
            filtered = filtered.filter(notice => 
                notice.title.toLowerCase().includes(searchQuery.toLowerCase()) ||
                notice.content.toLowerCase().includes(searchQuery.toLowerCase())
            );
        }

        // Filter by type
        if (typeFilter !== 'all') {
            filtered = filtered.filter(notice => notice.type === typeFilter);
        }

        // Filter by category
        if (categoryFilter !== 'all') {
            filtered = filtered.filter(notice => notice.category === categoryFilter);
        }

        setFilteredNotices(filtered);
    };

    const getTypeIcon = (type: string) => {
        switch (type) {
            case 'info': return faInfoCircle;
            case 'warning': return faExclamationTriangle;
            case 'success': return faCheckCircle;
            case 'error': return faExclamationTriangle;
            default: return faInfoCircle;
        }
    };

    const getTypeColor = (type: string) => {
        switch (type) {
            case 'info': return 'info';
            case 'warning': return 'warning';
            case 'success': return 'success';
            case 'error': return 'danger';
            default: return 'info';
        }
    };

    const getPriorityColor = (priority: string) => {
        switch (priority) {
            case 'high': return 'danger';
            case 'medium': return 'warning';
            case 'low': return 'success';
            default: return 'secondary';
        }
    };

    const formatDate = (dateString: string) => {
        return new Date(dateString).toLocaleDateString('en-US', {
            year: 'numeric',
            month: 'short',
            day: 'numeric',
            hour: '2-digit',
            minute: '2-digit'
        });
    };

    const markAsRead = (id: string) => {
        setNotices(prevNotices => 
            prevNotices.map(notice => 
                notice.id === id 
                    ? { ...notice, isRead: true }
                    : notice
            )
        );
    };

    const getUnreadCount = () => {
        return notices.filter(notice => !notice.isRead).length;
    };

    return (
        <>
            <DashboardHeader />
            <SideMenu />
            <div className="vertical-overlay" />
            
            <div className="main-content">
                <div className="page-content">
                    <div className="container-fluid">
                        {/* Header */}
                        <div className="row mb-4">
                            <div className="col-12">
                                <div className="d-flex align-items-center justify-content-between">
                                    <div>
                                        <h2 className="fw-bold mb-1">
                                            <FontAwesomeIcon icon={faBell} className="me-2 text-primary" />
                                            System Notices
                                        </h2>
                                        <p className="text-muted mb-0">
                                            Important announcements and updates for {clinicName}
                                        </p>
                                    </div>
                                    <div className="text-end">
                                        <div className="badge bg-primary fs-6 px-3 py-2">
                                            {getUnreadCount()} Unread
                                        </div>
                                    </div>
                                </div>
                            </div>
                        </div>

                        {/* Filters */}
                        <div className="row mb-4">
                            <div className="col-lg-6">
                                <div className="search-box">
                                    <div className="position-relative">
                                        <input
                                            type="text"
                                            className="form-control"
                                            placeholder="Search notices..."
                                            value={searchQuery}
                                            onChange={(e) => setSearchQuery(e.target.value)}
                                        />
                                        <FontAwesomeIcon 
                                            icon={faSearch} 
                                            className="search-icon position-absolute top-50 end-0 translate-middle-y me-3 text-muted"
                                        />
                                    </div>
                                </div>
                            </div>
                            <div className="col-lg-3">
                                <select
                                    className="form-select"
                                    value={typeFilter}
                                    onChange={(e) => setTypeFilter(e.target.value)}
                                >
                                    <option value="all">All Types</option>
                                    <option value="info">Info</option>
                                    <option value="warning">Warning</option>
                                    <option value="success">Success</option>
                                    <option value="error">Error</option>
                                </select>
                            </div>
                            <div className="col-lg-3">
                                <select
                                    className="form-select"
                                    value={categoryFilter}
                                    onChange={(e) => setCategoryFilter(e.target.value)}
                                >
                                    <option value="all">All Categories</option>
                                    <option value="system">System</option>
                                    <option value="maintenance">Maintenance</option>
                                    <option value="update">Update</option>
                                    <option value="announcement">Announcement</option>
                                </select>
                            </div>
                        </div>

                        {/* Notices List */}
                        <div className="row">
                            <div className="col-12">
                                {filteredNotices.map((notice) => (
                                    <div key={notice.id} className={`card border-0 shadow-sm mb-3 ${!notice.isRead ? 'border-start border-4 border-primary' : ''}`}>
                                        <div className="card-body">
                                            <div className="d-flex align-items-start">
                                                <div className="flex-shrink-0 me-3">
                                                    <FontAwesomeIcon 
                                                        icon={getTypeIcon(notice.type)} 
                                                        className={`text-${getTypeColor(notice.type)} fs-4`}
                                                    />
                                                </div>
                                                <div className="flex-grow-1">
                                                    <div className="d-flex align-items-center justify-content-between mb-2">
                                                        <h6 className="mb-0">{notice.title}</h6>
                                                        <div className="d-flex align-items-center gap-2">
                                                            {!notice.isRead && (
                                                                <span className="badge bg-primary">New</span>
                                                            )}
                                                            <span className={`badge bg-${getPriorityColor(notice.priority)}`}>
                                                                {notice.priority.toUpperCase()}
                                                            </span>
                                                            <span className="badge bg-secondary">
                                                                {notice.category.toUpperCase()}
                                                            </span>
                                                        </div>
                                                    </div>
                                                    <p className="text-muted mb-3">{notice.content}</p>
                                                    <div className="d-flex align-items-center justify-content-between">
                                                        <div className="d-flex align-items-center gap-3">
                                                            <small className="text-muted">
                                                                <FontAwesomeIcon icon={faClock} className="me-1" />
                                                                {formatDate(notice.createdAt)}
                                                            </small>
                                                            {notice.expiresAt && (
                                                                <small className="text-muted">
                                                                    Expires: {formatDate(notice.expiresAt)}
                                                                </small>
                                                            )}
                                                        </div>
                                                        <button
                                                            className="btn btn-sm btn-outline-primary"
                                                            onClick={() => markAsRead(notice.id)}
                                                        >
                                                            <FontAwesomeIcon icon={faEye} className="me-1" />
                                                            {notice.isRead ? 'Mark as Unread' : 'Mark as Read'}
                                                        </button>
                                                    </div>
                                                </div>
                                            </div>
                                        </div>
                                    </div>
                                ))}
                            </div>
                        </div>

                        {/* No Results */}
                        {filteredNotices.length === 0 && (
                            <div className="text-center py-5">
                                <FontAwesomeIcon icon={faBell} className="text-muted fs-1 mb-3" />
                                <h5 className="text-muted">No notices found</h5>
                                <p className="text-muted">
                                    Try adjusting your search terms or filters
                                </p>
                            </div>
                        )}
                    </div>
                </div>
            </div>
            
            <DashboardFooter />
        </>
    );
};

export default Notices;
